/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  Text,
  Container,
  Flex,
  Divider,
  Icon,
  HStack,
  Button,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaShieldAlt,
  FaHandshake,
  FaMoneyBillWave,
  FaUsers,
  FaMoneyCheckAlt,
  FaUserCog,
  FaMousePointer,
  FaUserCheck,
  FaClipboardList,
} from 'react-icons/fa';
import { Navigate } from 'react-router-dom';
import TesseramentoForm from './TesseramentoForm';
import tessera2025 from '../images/tessera2025.png';

const Tesseramento = ({ isAuthenticated, userData }) => {
  const [showForm, setShowForm] = useState(false);
  // Se l'utente è autenticato, reindirizza alla sua area tesseramenti
  if (isAuthenticated) {
    return <Navigate to="/account/tesseramenti" replace />;
  }

  return (
    <Container maxW="container.xl" py={8} bg="gray.100">
      <VStack
        spacing={6}
        textAlign="center"
        maxW="container.md"
        margin="0 auto"
      >
        <Box
          position="relative"
          w="100%"
          borderRadius="lg"
          overflow="hidden"
          boxShadow="xl"
          minH={{ base: 'auto', md: '350px' }} // Altezza minima solo su desktop
        >
          {/* Immagine di sfondo */}
          <Box
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            backgroundImage={`url(${tessera2025})`}
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
          >
            {/* Overlay opaco per contrasto */}
            <Box
              position="absolute"
              top="0"
              left="0"
              w="100%"
              h="100%"
              bg="rgba(0, 0, 0, 0.6)"
            />
          </Box>

          {/* Contenuto sopra lo sfondo */}
          <Box
            position="relative"
            zIndex="2"
            p={6}
            color="white"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            minH={{ base: 'auto', md: '350px' }}
          >
            <Text fontSize="lg" fontWeight="bold">
              Per diventare socio di{' '}
              <b style={{ color: '#FFD700' }}>CIL – Creatività in Libertà</b>,
              compila il modulo qui sotto con i tuoi dati.
            </Text>
            <Divider my={4} borderColor="gray.300" />
            <Flex alignItems="center" gap={2}>
              <Icon as={FaCheckCircle} color="yellow.300" />
              <Text fontSize="md" textAlign="left">
                Quota annuale: <b>10€</b>
              </Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Icon as={FaCheckCircle} color="yellow.300" />
              <Text fontSize="md" textAlign="left">
                Include adesione e copertura assicurativa
              </Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Icon as={FaCheckCircle} color="yellow.300" />
              <Text fontSize="md" textAlign="left">
                Pagamento online o in sede
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* Bottone principale - subito visibile */}
        {!showForm && (
          <Box w="100%" py={6}>
            <Center>
              <Button
                onClick={() => {
                  setShowForm(true);
                }}
                size="lg"
                height="60px"
                px={10}
                fontSize="xl"
                fontWeight="bold"
                bg="brand.100"
                color="white"
                _hover={{ bg: 'brand.200', transform: 'scale(1.05)' }}
                _active={{ transform: 'scale(.98)' }}
                boxShadow="xl"
                borderRadius="lg"
              >
                Diventa Socio
              </Button>
            </Center>
          </Box>
        )}

        {/* Form o contenuto informativo */}
        {showForm ? (
          <TesseramentoForm
            isAuthenticated={isAuthenticated}
            userData={userData}
          />
        ) : (
          <Box p={6} bg="white" borderRadius="md" boxShadow="md">
            <Heading
              as="h2"
              size="lg"
              textAlign="center"
              color="teal.600"
              mb={6}
            >
              Come Tesserarsi a CIL - Creatività in libertà
            </Heading>
            <Divider mb={8} />
            <VStack spacing={6} align="start">
              {/* Step 1 */}
              <HStack align="start" justify="start" spacing={4}>
                <Icon as={FaMousePointer} boxSize={6} color="teal.500" />
                <Box textAlign="left">
                  <Heading
                    as="h4"
                    size="sm"
                    mb={1}
                    color="teal.600"
                    textAlign="left"
                  >
                    1. Clicca su &quot;Diventa Socio&quot;
                  </Heading>
                  <Text fontSize="sm" textAlign="left">
                    Clicca sul bottone &quot;Diventa Socio&quot; qui sopra per
                    iniziare il processo di tesseramento, oppure accedi alla tua
                    area riservata da &quot;Il Mio Account&quot; e clicca su
                    &quot;+ in Tesseramenti&quot;.
                  </Text>
                </Box>
              </HStack>

              {/* Step 2 */}
              <HStack align="start" justify="start" spacing={4}>
                <Icon as={FaUserCheck} boxSize={6} color="teal.500" />
                <Box textAlign="left">
                  <Heading as="h4" size="sm" mb={1} color="teal.600">
                    2. Seleziona il tipo di socio
                  </Heading>
                  <Text fontSize="sm">
                    Indica se si vuole tesserare un socio minore o adulto. Non è
                    possibile aggiungere un secondo socio adulto con la stessa
                    email.
                  </Text>
                </Box>
              </HStack>

              {/* Step 3 */}
              <HStack align="start" justify="start" spacing={4}>
                <Icon as={FaClipboardList} boxSize={6} color="teal.500" />
                <Box textAlign="left">
                  <Heading as="h4" size="sm" mb={1} color="teal.600">
                    3. Compila i form
                  </Heading>
                  <Text fontSize="sm">
                    Compila tutti i form richiesti fino alla fine, fornendo le
                    informazioni necessarie.
                  </Text>
                </Box>
              </HStack>

              {/* Step 4 */}
              <HStack align="start" justify="start" spacing={4}>
                <Icon as={FaUserCog} boxSize={6} color="teal.500" />
                <Box textAlign="left">
                  <Heading as="h4" size="sm" mb={1} color="teal.600">
                    4. Accedi alla tua area personale
                  </Heading>
                  <Text fontSize="sm">
                    Al termine della registrazione verrai indirizzato alla tua
                    area personale. Riceverai un&apos;email per impostare la
                    password del tuo account.
                  </Text>
                </Box>
              </HStack>

              {/* Step 5 */}
              <HStack align="start" justify="start" spacing={4}>
                <Icon as={FaMoneyCheckAlt} boxSize={6} color="teal.500" />
                <Box textAlign="left">
                  <Heading as="h4" size="sm" mb={1} color="teal.600">
                    5. Paga la tessera
                  </Heading>
                  <Text fontSize="sm">
                    Per completare l&apos;iscrizione, effettua il pagamento
                    della tessera cliccando su &quot;Paga Ora&quot;.
                  </Text>
                </Box>
              </HStack>

              {/* Step 6 */}
              <Alert status="warning" variant="left-accent">
                <AlertIcon />
                <Box textAlign="left">
                  <AlertTitle>Attenzione!</AlertTitle>
                  <AlertDescription>
                    Il tesseramento dev&apos;essere fatto per il socio che
                    parteciperà alle attività dell&apos;associazione. Se stai
                    tesserando un minore, selezione Minore nel tipo di socio.
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </Box>
        )}

        <Box w="100%" bg="gray.100" borderRadius={8}>
          <Container maxW="7xl" py={{ base: 12, md: 20 }}>
            <Flex direction="column" align="center" textAlign="center">
              {/* Titolo */}
              <Heading as="h2" size="xl" color="blue.700" mb={4}>
                🤝 Perché il tesseramento è importante?
              </Heading>

              {/* Testo introduttivo */}
              <Text fontSize="lg" color="gray.700" maxW="3xl">
                Essere parte di{' '}
                <b style={{ color: '#3b235a' }}>CIL – Creatività in Libertà</b>
                &nbsp;significa supportare un&apos;associazione che investe in{' '}
                <b>spazi, materiali, eventi e inclusività</b>. La tua quota di
                iscrizione è fondamentale per mantenere viva la nostra community
                e garantire esperienze di qualità per tutti.
              </Text>

              <Divider my={6} borderColor="blue.300" />

              {/* Sezione con i motivi del tesseramento */}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align="center"
                gap={6}
                w="100%"
              >
                {/* 1️⃣ Affitto Spazi */}
                <Flex
                  direction="column"
                  align="center"
                  bg="white"
                  p={6}
                  borderRadius="lg"
                  boxShadow="xl"
                  w={{ base: '100%', md: '48%' }}
                  _hover={{ transform: 'scale(1.05)', transition: '0.3s' }}
                >
                  <Icon as={FaUsers} w={12} h={12} color="blue.500" />
                  <Text fontSize="lg" fontWeight="bold" mt={2}>
                    Affitto degli spazi
                  </Text>
                  <Text fontSize="md" color="gray.600" mt={2}>
                    CIL si impegna a offrire un luogo accogliente per
                    incontrarsi. Il tuo tesseramento aiuta a coprire i costi di
                    affitto e manutenzione della sede.
                  </Text>
                </Flex>

                {/* 2️⃣ Materiali per le attività */}
                <Flex
                  direction="column"
                  align="center"
                  bg="white"
                  p={6}
                  borderRadius="lg"
                  boxShadow="xl"
                  w={{ base: '100%', md: '48%' }}
                  _hover={{ transform: 'scale(1.05)', transition: '0.3s' }}
                >
                  <Icon as={FaMoneyBillWave} w={12} h={12} color="yellow.400" />
                  <Text fontSize="lg" fontWeight="bold" mt={2}>
                    Materiali e attrezzature
                  </Text>
                  <Text fontSize="md" color="gray.600" mt={2}>
                    Giochi da tavolo, carte Magic, strumenti per laboratori
                    artistici: tutto questo ha un costo. Il tuo supporto ci
                    permette di migliorare continuamente le attività.
                  </Text>
                </Flex>
              </Flex>

              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align="center"
                gap={6}
                w="100%"
                mt={6}
              >
                {/* 3️⃣ Assicurazione e sicurezza */}
                <Flex
                  direction="column"
                  align="center"
                  bg="white"
                  p={6}
                  borderRadius="lg"
                  boxShadow="xl"
                  w={{ base: '100%', md: '48%' }}
                  _hover={{ transform: 'scale(1.05)', transition: '0.3s' }}
                >
                  <Icon as={FaShieldAlt} w={12} h={12} color="green.500" />
                  <Text fontSize="lg" fontWeight="bold" mt={2}>
                    Assicurazione e sicurezza
                  </Text>
                  <Text fontSize="md" color="gray.600" mt={2}>
                    Il tesseramento include una copertura assicurativa,
                    garantendo sicurezza durante tutte le attività e protezione
                    per i soci.
                  </Text>
                </Flex>

                {/* 4️⃣ Inclusione e progetti sociali */}
                <Flex
                  direction="column"
                  align="center"
                  bg="white"
                  p={6}
                  borderRadius="lg"
                  boxShadow="xl"
                  w={{ base: '100%', md: '48%' }}
                  _hover={{ transform: 'scale(1.05)', transition: '0.3s' }}
                >
                  <Icon as={FaHandshake} w={12} h={12} color="purple.500" />
                  <Text fontSize="lg" fontWeight="bold" mt={2}>
                    Progetti inclusivi
                  </Text>
                  <Text fontSize="md" color="gray.600" mt={2}>
                    Con il tuo contributo, possiamo organizzare eventi e
                    iniziative gratuite per chi ha meno possibilità, rendendo il
                    nostro spazio sempre più aperto a tutti.
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Container>
        </Box>
      </VStack>
    </Container>
  );
};

export default Tesseramento;
